/**
 * @module Experince/Product-Selector
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */
// Core Libraries
import { Component, OnDestroy, OnInit, EventEmitter } from '@angular/core';
import { SimValidation } from '@support/api/proxies/responses/api-108.interface';
import { ObjectInterface } from '@support/api/common/api-response.interface';
import { TmoButton } from '@support/shared/widgets/interfaces/button.interface';
import {
  FamilyPlanResponse,
  VoucherContent,
  VoucherDetails,
} from '@experience/shop/softgoods/interfaces/family-plan.interface';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Cart, CartResponse } from '@support/api/proxies/responses/api-cart-response.interface';

import { BrowsePlanServiceService } from '@experience/shared-experience/services/browse-plan-service.service';
import { CartSupportService } from '@support/cart-support/services/cart-support.service';
import { FormCreationService } from '@support/shared/services/form-creation.service';
import { CommonApiService } from '@support/shared/services/common-api/common-api.service';
import { TileManagementService } from '@support/shared/services/tile-management.service';
import { APIError } from '@support/api/common/error.interface';
import { Observable, Subject, of as observableOf, throwError as observableThrowError } from 'rxjs';
import { map, catchError, takeUntil } from 'rxjs/operators';
import { lineTypeEnum } from '@experience/shop/product-selector/interfaces/product-selector.interface';
import { ORDER_TYPE } from '@experience/shop/shop.constants';
import { ActivatedRoute, Router } from '@angular/router';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { CartValidatedItem } from '@experience/shop/cart/interfaces/cart-review.interface';
import { BillingTypeEnum } from '@experience/landing/common/constants/account-landing.constants';
import { ContentProviderService } from '@core/aem/services/content-provider.service';
import { ValidationMessageModel } from '@experience/shop/shared-browse/models/validation-message.model';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { NavbarService } from '@root/shared/services/navbar.service';
// Interfaces
import { DynamicModal } from '@support/shared/widgets/interfaces/dynamic-modal.interface';
// Constants
import { PLAN_PRODUCT_CONSTANTS } from '@core/aem/components/plan-product-offering/plan-product-constant/plan-product-offering.constants';

/*tslint:disable:max-file-line-count no-any*/
@Component({
  selector: 'tmo-pre-purchased-plan-component',
  templateUrl: './pre-purchased-plan.component.html',
})
export class PrePurchasedPlanComponent implements OnInit, OnDestroy {
  /**
   * Stores Object for fieldValidationConstants
   */
  public fieldValidationConstants: ObjectInterface;
  /**
   * Stores sim number
   */
  public validatedSimNumber: string;
  /** Primary CTA */
  public continue: TmoButton;
  /**
   * Variable to hold plans data
   */
  public plansData: FamilyPlanResponse;
  /**
   * Form to control simNumber entry
   */
  public simValidationForm: UntypedFormGroup;
  /**
   * Cart data with plan added
   */
  public cartData: Cart;
  /**
   * Sim number valid flag
   */
  public validSIMNumber: boolean;
  /**
   * Flag to disable the continue button
   */
  public disableContinue: boolean;
  /**
   * plan option selector id of plan in focus
   */
  public planOptionSelectorId: string;
  /**
   * Sim validation response with sim details
   */
  public simDetails: SimValidation;
  /**
   * Configurable content for the screen
   * @TODO US335615 to discuss and write a generic config data  to match authorable and configurable content
   */
  public configurableAEMContent: any;
  /**
   * Authorable content for the screen
   * @TODO US335615 to discuss and write a generic config data  to match authorable and configurable content
   */
  public prePurchasedPlanAuthorableContent: any;
  /**
   * Flag to disable services till plan response with cart is received
   */
  public disableServices: boolean;
  /**
   * Authorable content for legal text
   * @TODO US335615 to discuss and write a generic config data  to match authorable and configurable content
   */
  public legalTextAuthorableContent: any[];
  /**
   * Model to hold sim valid status and sim error messages
   */
  public simValidationMessage: ValidationMessageModel;
  /**
   * flag to control spinner
   */
  public showSpinner: boolean;
  /**
   * Event Emitter to trigger events on click of anchor
   */
  public triggerEvent: EventEmitter<Object>;
  /**
   * Flag to see open or close modal
   */
  public seeMoreModal: boolean;
  /**
   * Modal to store dynamic data
   */
  public modal: DynamicModal;
  /**
   * Flag to be displayed in case of plan fetch error
   */
  public error: APIError;
  /**
   * To clear out all the subscribers
   */
  public ngUnsubscribe: Subject<void>;
  /**
   * To check if the voucher is for tourist
   */
  public isTourist: boolean;
  /**
   * AsyncValidator to test sim validity and display errors
   * @param {AbstractControl} control
   * @returns {Observable<{length: boolean}>}
   */
  // tslint:disable next-line proper-class-var-init
  public isEligibleSIMNumber = (control: AbstractControl) => {
    const cleanedSimNumberLength = 19;
    if (control.value.length !== cleanedSimNumberLength) {
      this.validSIMNumber = false;
      return observableOf({ length: false });
    }
    this.showSpinner = true;
    return this.cartSupportService.validateSIMNumber(control.value).pipe(
      takeUntil(this.ngUnsubscribe.asObservable()),
      map((response: SimValidation) => {
        this.error = null;
        this.showSpinner = false;
        if (response.simValid === true) {
          this.simValidationMessage.setMessage(
            this.objectService.get(this.configurableAEMContent, 'simValidationSuccessMessage')
          );
          this.simValidationMessage.setSuccess(true);
          this.simDetails = response;
          this.validSIMNumber = true;
          this.disableServices = false;
          return null;
        } else {
          this.validSIMNumber = false;
          this.simValidationMessage.setMessage(
            this.objectService.get(this.configurableAEMContent, 'simInUseMessage')
          );
          this.simValidationMessage.setSuccess(false);
          return observableThrowError({ control: false });
        }
      }),
      catchError((err: APIError) => {
        this.showSpinner = false;
        this.validSIMNumber = false;
        this.error = err;
        this.simValidationMessage.setSuccess(false);
        return observableThrowError({ control: false });
      })
    );
  };

  constructor(
    private browsePlanServiceService: BrowsePlanServiceService,
    private stateStoreUtilService: StateStoreUtilsService,
    private cartSupportService: CartSupportService,
    private formCreationService: FormCreationService,
    private commonApiService: CommonApiService,
    private contentProviderService: ContentProviderService,
    private formBuilder: UntypedFormBuilder,
    private tileManagementService: TileManagementService,
    private objectService: ObjectService,
    private router: Router,
    private navbarService: NavbarService,
    private route: ActivatedRoute,
    public routeSnapshot: ActivatedRoute
  ) {
    this.ngUnsubscribe = new Subject<void>();
    this.validSIMNumber = false;
    this.disableContinue = true;
    this.disableServices = true;
    this.simValidationMessage = new ValidationMessageModel();
    this.seeMoreModal = false;
    this.modal = {
      disableDismiss: false,
      disableEsc: true,
      toggleModalEmitter: new EventEmitter(),
      dismissableMask: true,
      modalName: 'pre-purchased-plan-modal',
    };
  }
  /**
   * Init lifecycle hook
   */
  public ngOnInit(): void {
    this.isTourist =
      this.objectService.get(this.route.snapshot.queryParams, 'voucher', '').toLowerCase() ===
      PLAN_PRODUCT_CONSTANTS.TOURIST;
    this.commonApiService.uiFieldValidationsLoaded$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((data: boolean) => {
        this.fieldValidationConstants = this.commonApiService.getFieldValidationConstants();
      });
    this.simValidationForm = this.buildSimNumberForm();
    this.simValidationForm.controls.simNumber.setAsyncValidators([this.isEligibleSIMNumber]);
    this.validatedSimNumber = this.stateStoreUtilService.getSimNumber();
    if (this.validatedSimNumber) {
      this.simValidationForm.controls.simNumber.setValue(`${this.validatedSimNumber}F`);
    }
    const customerConfigData = this.stateStoreUtilService.getCustomerConfigData() || {};
    customerConfigData.billingType = BillingTypeEnum.PREPAID;
    // @TODO as per TA discussion for bestbuy the zip code to be defaulted is to 98007
    customerConfigData.zip = '98007';
    this.stateStoreUtilService.setCustomerConfigData(customerConfigData);
    this.initButtons();
    this.initAEMData();
    this.getPrePurchasedPlanForVoucher();
    this.simValidationForm.controls.simNumber.valueChanges
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe((object: string) => {
        this.simValidationMessage.setSuccess(false);
        this.simValidationMessage.setMessage('');
      });
  }
  /**
   * destroy lifecycle hook
   */
  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
  /**
   * Function to fetch pre-purchased plans by voucher
   */
  public getPrePurchasedPlanForVoucher(): void {
    this.showSpinner = true;
    this.browsePlanServiceService
      .getBrowsePlans()
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe(
        (plansData: FamilyPlanResponse) => {
          this.plansData = plansData;
          this.planOptionSelectorId = this.objectService.get(
            plansData,
            `familyPlans.0.planOptions.0.planOptionSelectorId`
          );
          const isVoucherUsageInProgress =
            this.stateStoreUtilService.getPrePurchasedPlanVoucherDetails();
          this.disableServices = !isVoucherUsageInProgress;
          if (isVoucherUsageInProgress) {
            this.tileManagementService.getUpdatedData();
          }
          if (this.planOptionSelectorId && !isVoucherUsageInProgress) {
            const lineNumber = this.tileManagementService.generateTile(
              lineTypeEnum.LINE,
              true
            ).tileId;
            const transactionTypes = ORDER_TYPE.ACTIVATION;
            this.cartSupportService
              .putSelectedPlanToCart(
                this.planOptionSelectorId,
                lineNumber,
                false,
                transactionTypes,
                null
              )
              .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
              .subscribe(
                (response: CartResponse) => {
                  this.showSpinner = false;
                  this.setVoucherDetails(
                    this.objectService.get(response, 'cart.lines.0.plan.0.offerId', '')
                  );
                  this.navbarService.hideGlobalNavEmitter.emit(true);
                  this.tileManagementService.createTile();
                  this.tileManagementService.mapToCartResponse(response.cart);
                  this.tileManagementService.getUpdatedData();
                  this.cartData = response.cart;
                },
                (err: APIError) => {
                  this.showSpinner = false;
                  // Navigate to Error page, if Error during plan addition in cart
                  this.router.navigate(['/error']);
                }
              );
          } else if (!this.planOptionSelectorId) {
            this.router.navigate(['/error']);
          } else {
            this.showSpinner = false;
            this.cartData = this.stateStoreUtilService.getCart();
          }
        },
        (err: APIError) => {
          this.error = err;
          this.plansData = undefined;
          this.showSpinner = false;
          // Navigate to Error page, if Error during plan fetching
          this.router.navigate(['/error']);
        }
      );
  }

  /**
   * Function to set pre purchased voucher details
   * @param offerId : offer id of plan
   */
  private setVoucherDetails(offerId: string): void {
    let touristPlans;
    const planPermissionDetails = this.objectService.get(
      this.configurableAEMContent,
      'planPermissionDetails',
      []
    );
    if (planPermissionDetails.length) {
      touristPlans = JSON.parse(planPermissionDetails);
    }
    const touristPlan =
      touristPlans &&
      touristPlans.filter((planPermission: VoucherContent) => {
        return planPermission.offerCode === offerId;
      });
    const prePurchasedPlanDetails: VoucherDetails = {
      voucher: this.route.snapshot.queryParams.voucher,
      cart: {
        edit:
          this.objectService.get(touristPlan, '0.cart.edit', '').toLocaleUpperCase() ===
          PLAN_PRODUCT_CONSTANTS.TRUE,
        duplicate:
          this.objectService.get(touristPlan, '0.cart.duplicate', '').toLocaleUpperCase() ===
          PLAN_PRODUCT_CONSTANTS.TRUE,
        remove:
          this.objectService.get(touristPlan, '0.cart.remove', '').toLocaleUpperCase() ===
          PLAN_PRODUCT_CONSTANTS.TRUE,
      },
      payment: {
        hidePaymentSection:
          this.objectService
            .get(touristPlan, '0.payment.hidePaymentSection', '')
            .toLocaleUpperCase() === PLAN_PRODUCT_CONSTANTS.TRUE,
      },
    };
    this.stateStoreUtilService.setPrePurchasedPlanVoucherDetails(prePurchasedPlanDetails);
  }
  /**
   * Function to navigate to cart review screen
   */
  public goToCartReviewScreen(): void {
    const customerConfigData = this.stateStoreUtilService.getCustomerConfigData();
    customerConfigData.zip = '';
    this.stateStoreUtilService.setCustomerConfigData(customerConfigData);
    const cart = this.stateStoreUtilService.getCart();
    const payLoad: CartValidatedItem[] = [
      {
        cartItemId: this.objectService.get(cart, `lines.0.plan.0.id`),
        validatedSimImsi: this.objectService.get(this.simDetails, 'imsi'),
        validatedSim:
          this.objectService.get(this.simDetails, 'simNumber') || this.validatedSimNumber,
      },
    ];

    this.cartSupportService
      .postValidatedItemsToCart(payLoad)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((response: CartResponse) => {
        this.router.navigate(['/cart/review']);
      });
  }

  /**
   * Showing of modal on toggle event
   * @param event : toggle event
   * @returns void
   */
  public showSeeMoreModal(event?: EventEmitter<Object>): void {
    this.seeMoreModal = true;
    this.triggerEvent = event;
    this.modal.toggleModalEmitter.emit({
      open: true,
      close: false,
      dismissableMask: true,
    });
  }
  /**
   * Method to handle Click event
   * @returns void
   */
  public closeModal(): void {
    const target = PLAN_PRODUCT_CONSTANTS.TARGET;
    this.triggerEvent[target].focus();
    this.modal.toggleModalEmitter.emit({
      open: false,
      close: true,
    });
    this.seeMoreModal = false;
  }
  /**
   * Retrieves AEM content for product selector landing component
   * @returns {void}
   */
  private initAEMData(): void {
    // Authorable && Configurable content from product-selector to be used for legal_text and content data
    this.contentProviderService
      .getContentData('product-selector')
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((contentData: any[]) => {
        this.configurableAEMContent = this.contentProviderService.getConfigurableData(contentData);
        this.legalTextAuthorableContent = contentData.filter((listOfContent: any) => {
          return listOfContent.uiComponent === 'legal-text';
        });
      });
    // Authorable content for the page
    this.contentProviderService
      .getContentData()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((response: any) => {
        this.prePurchasedPlanAuthorableContent =
          this.contentProviderService.getAuthorableData(response);
      });
  }
  /**
   * Function to build simNumber form
   * @returns {FormGroup}
   */
  private buildSimNumberForm(): UntypedFormGroup {
    const fieldValidationConstants = this.commonApiService.getFieldValidationConstants();
    const simNumber = this.objectService.get(
      this.stateStoreUtilService.getCart(),
      'lines.0.simNumber'
    )
      ? `${this.objectService.get(this.stateStoreUtilService.getCart(), 'lines.0.simNumber')}`
      : undefined;
    return this.formBuilder.group({
      simNumber: this.formCreationService.getFormControl(
        fieldValidationConstants.SIM,
        simNumber || '',
        true
      ),
    });
  }
  /**
   * Initialize Button Name
   */
  private initButtons(): void {
    this.continue = {
      name: 'Continue',
      id: 'backToTop',
      class: 'btn btn-primary',
      type: 'continue',
    };
  }
}
